import ApplicationGalleryTile from '@/components/inspiration/galleries/application_gallery_tile';
import PageLayout, { siteTitle } from "@/components/PageLayout";
import SearchBuilder from "@/components/search_builder.js";
import DropdownFilter from "@/components/dropdown_filter";
import PhotoFilterPillBar from "@/components/inspiration/photo_filter_pill_bar";
import { useState, useReducer, useEffect } from "react";
import Qs from "qs";
import GalleryService from '@/lib/services/galleries';
import searchTranslator from "@/lib/search_translator.js";
import { loadConfig } from "@/lib/utils.js";
import Link from "next/link";
import SearchToggle from '@/components/inspiration/search_toggle';
import { is_mobile } from '@/lib/utils.js';

export default function ApplicationPhotoSearchPage(results){
  const {
    galleries, 
    total_count,
    initialSearch,
    page_layout,
    favicon,
    stylesheet,
    cart_enabled,
    application_category_filters,
    space_filters,
    product_type_filters,
    next_page,
    contained_product_name,
    inspiration_display,
    mobile
  } = results;

  const [isLoading, setIsLoading] = useState(false);
  const [appGalleries, setAppGalleries] = useState(galleries);
  const [totalCount, setTotalCount] = useState(total_count);
  const [applicationCategoryFilters, setApplicationCategoryFilters] = useState(application_category_filters)
  const [spaceFilters, setSpaceFilters] = useState(space_filters)
  const [productTypeFilters, setProductTypeFilters] = useState(product_type_filters)
  const [nextPage, setNextPage] = useState(next_page)

  const galleryFilters = [
    ['photos_application_categories_id_eq', 'Application Types', applicationCategoryFilters], 
    ['photos_application_spaces_id_eq', 'Spaces', spaceFilters], 
    ['photos_product_hot_spots_product_tags_id_eq', 'Product Types', productTypeFilters]
  ]

  const searchGalleries = (search, pageChange) => {
    setIsLoading((currentIsLoaded) => true);
    fetch(
      "/lex-api/inspiration/galleries/all" +
        "?" +
        Qs.stringify(search, { arrayFormat: "brackets", encode: true })
    )
      .then((res) => res.json())
      .then((data) => {
        setIsLoading((currentIsLoaded) => false);
        setAppGalleries(data.galleries);
        setTotalCount(data.total_count);
        setApplicationCategoryFilters(data.application_category_filters);
        setSpaceFilters(data.space_filters);
        setProductTypeFilters(data.product_type_filters);
        setNextPage(data.next_page);
      });
  };

  const loadMoreGalleries = (currentSearch) => {
    setIsLoading((currentIsLoaded) => true);
    let search = currentSearch || {};
    search['page'] = nextPage;
    fetch(
      "/lex-api/inspiration/galleries/all" +
        "?" +
        Qs.stringify(search, { arrayFormat: "brackets", encode: true })
    )
      .then((res) => res.json())
      .then((data) => {
        setIsLoading((currentIsLoaded) => false);
        setAppGalleries((galleries) => galleries.concat(data.galleries));
        setNextPage(data.next_page);
      });
  }

  return (
    <PageLayout
      title="Inspiration Search"
      page_layout={page_layout}
      cart_enabled={cart_enabled}
      favicon={favicon}
      stylesheet={stylesheet}
    >
      <SearchBuilder
        initialSearch={initialSearch}
        search={searchGalleries}
      >
        { (searchState) => {
          return (
            <div className='lexcms-inspiration'>
              <div className='lexcms-inspiration-search-container'>
                <div className="lexcms-inspiration-search-header-text">
                  <h1>Inspirational Search</h1>
                  <p>Discover awe-inspiring images from our projects.</p>
                </div>
                <div className='lexcms-inspiration-sidebar-filters'>
                  { inspiration_display == 'both_application_photos_and_galleries' &&
                    <SearchToggle display={inspiration_display} galleriesSelected={true} />
                  }
                  <p className='lexcms-inspiration-search-refine-photos-count'>Refine results:</p>
                  { galleryFilters.map((d, i) => {
                      return (<DropdownFilter
                        key={i}
                        searchKey={`q[${d[0]}]`}
                        className={"inspiration-search-sidebar-filter"}
                        searchState={searchState}
                        options={d[2]}
                        label={d[1]}
                        disableFirst={true}
                      />)
                    })
                  }
                  <Link href='/lex-cms/inspiration/highlights'>
                    <a className='lexcms-regular-button lexcms-clear-photos-button' 
                      onClick={() => {
                        searchGalleries({});
                        searchState.clearAll();
                      }}>Reset All Selections</a>
                  </Link>
                </div>
              </div>
              <div className='lexcms-search-results app-gallery-container'>
                <div className='lexcms-filters-container lexcms-results-top'>
                  <PhotoFilterPillBar 
                    searchState={searchState} 
                    application_categories={applicationCategoryFilters}
                    space_filters={spaceFilters}
                    product_type_filters={productTypeFilters}
                    resourceName={'galleries'} 
                    filters={galleryFilters}
                    total_count={totalCount}
                    searchGalleries={searchGalleries}
                    contained_product_name={contained_product_name} /> 
                  <div className='lexcms-inspiration-search-sort-container'>
                    <DropdownFilter
                      searchKey={"q[s]"}
                      className={"inspiration-search-sort-filter"}
                      searchState={searchState}
                      options={[{ value: 'created_at DESC', label: 'Latest' }, 
                        { value: 'name ASC', label: 'A-Z' },
                        { value: 'name DESC', label: 'Z-A' }]}
                      label="Sort"
                      disableFirst={true}
                    />
                  </div>
                </div>
                <div className='lexcms-result-container'>
                  <div className='app-gallery-list'>
                    {appGalleries?.map((d ,i) => {
                      return (
                        <ApplicationGalleryTile app_gallery={d} key={d.id} index={i} mobile={mobile} />
                      ); 
                    })}
                  </div>
                </div>
                { nextPage && 
                <div className='galleries-remaining-link'> 
                  <a style={{cursor: 'pointer'}} onClick={() => { loadMoreGalleries(searchState.search) }}>Load More Galleries</a> 
                </div>
                }
              </div>
            </div>
          )
        } }
      </SearchBuilder>
    </PageLayout>
  )
}

export async function getServerSideProps(context) {
 let token = context?.req?.headers['lex-api-token'];

  let service = await GalleryService.build(
    token,
    context.req?.headers.host
  );
  let q = searchTranslator(context.query);
  let apiFriendlyQ = searchTranslator(context.query, true);
  let search = await service.index(apiFriendlyQ);
  search.initialSearch = q;
  let mobile = is_mobile(context.req?.headers['user-agent']);

  let pageConfig = loadConfig(service);
  return {
    props: { ...search, ...pageConfig, mobile: mobile },
  };
}
